export class ReceiveMessageWindow {
    #capacity;
    #accepted = new Set();
    #highest;
    constructor(capacity = 1000) {
        if (!Number.isSafeInteger(capacity) || capacity < 1)
            throw new RangeError("Receive window capacity must be positive");
        this.#capacity = capacity;
    }
    claim(messageId) {
        if (this.#accepted.has(messageId))
            return false;
        if (this.#highest !== undefined && messageId < this.#highest && this.#accepted.size >= this.#capacity) {
            const minimum = this.minimum;
            if (minimum !== undefined && messageId < minimum)
                return false;
        }
        this.#accepted.add(messageId);
        if (this.#highest === undefined || messageId > this.#highest)
            this.#highest = messageId;
        if (this.#accepted.size > this.#capacity) {
            const ordered = [...this.#accepted].sort((left, right) => left < right ? -1 : left > right ? 1 : 0);
            for (let index = 0; index < ordered.length - this.#capacity; index += 1)
                this.#accepted.delete(ordered[index]);
        }
        return true;
    }
    has(messageId) {
        return this.#accepted.has(messageId);
    }
    get minimum() {
        let minimum;
        for (const value of this.#accepted)
            if (minimum === undefined || value < minimum)
                minimum = value;
        return minimum;
    }
    checkpoint() {
        return { accepted: [...this.#accepted], highest: this.#highest };
    }
    restore(checkpoint) {
        this.#accepted.clear();
        for (const messageId of checkpoint.accepted)
            this.#accepted.add(messageId);
        this.#highest = checkpoint.highest;
    }
}
export class MessageIdGenerator {
    #last = 0n;
    next(estimatedServerUnixMillis, randomLowBits, modulo) {
        if (!Number.isFinite(estimatedServerUnixMillis) || !Number.isSafeInteger(randomLowBits) || randomLowBits < 0 || randomLowBits > 0x3fffffff) {
            throw new RangeError("Invalid message-ID clock input");
        }
        const seconds = BigInt(Math.floor(estimatedServerUnixMillis / 1000));
        const millis = BigInt(Math.floor(estimatedServerUnixMillis % 1000));
        let candidate = (seconds << 32n) | ((millis << 32n) / 1000n) | (BigInt(randomLowBits) << 2n) | BigInt(modulo);
        candidate = (candidate & ~3n) | BigInt(modulo);
        if (candidate <= this.#last)
            candidate = ((this.#last + 4n) & ~3n) | BigInt(modulo);
        this.#last = candidate;
        return candidate;
    }
}
export class SequenceNumberGenerator {
    #contentCount = 0;
    next(contentRelated) {
        const sequenceNumber = this.#contentCount * 2 + (contentRelated ? 1 : 0);
        if (contentRelated)
            this.#contentCount += 1;
        return sequenceNumber;
    }
}
export class AuthenticatedServerClock {
    monotonic;
    #serverUnixMilliseconds;
    #monotonicMilliseconds;
    constructor(monotonic) {
        this.monotonic = monotonic;
    }
    sample(serverUnixSeconds) {
        if (!Number.isSafeInteger(serverUnixSeconds) || serverUnixSeconds <= 0) {
            throw new RangeError("Invalid authenticated server-time sample");
        }
        const monotonicMilliseconds = this.monotonic.nowMilliseconds();
        if (!Number.isFinite(monotonicMilliseconds))
            throw new RangeError("Invalid monotonic clock");
        this.#serverUnixMilliseconds = serverUnixSeconds * 1000;
        this.#monotonicMilliseconds = monotonicMilliseconds;
    }
    sampleMessageId(serverMessageId) {
        if ((serverMessageId & 1n) !== 1n)
            throw new RangeError("Invalid authenticated server message ID");
        this.sample(Number(serverMessageId >> 32n));
    }
    nowMilliseconds() {
        if (this.#serverUnixMilliseconds === undefined || this.#monotonicMilliseconds === undefined) {
            throw new RangeError("Authenticated server time is unavailable");
        }
        const elapsed = this.monotonic.nowMilliseconds() - this.#monotonicMilliseconds;
        if (!Number.isFinite(elapsed) || elapsed < 0)
            throw new RangeError("Monotonic clock discontinuity");
        return this.#serverUnixMilliseconds + elapsed;
    }
}
export const validateInboundMessageId = (messageId, direction, nowSeconds) => {
    if (messageId === 0n || !Number.isFinite(nowSeconds))
        return { kind: "bad", errorCode: 18 };
    const validDirection = direction === "client"
        ? (messageId & 3n) === 0n && (messageId & 0xffffffffn) !== 0n
        : (messageId & 1n) === 1n;
    if (!validDirection)
        return { kind: "bad", errorCode: 18 };
    const seconds = Number(messageId >> 32n);
    if (seconds > nowSeconds + 30)
        return { kind: "bad", errorCode: 17 };
    if (seconds < nowSeconds - 300)
        return { kind: "bad", errorCode: 20 };
    return { kind: "valid" };
};
export class ReceiveSequenceValidator {
    #received = [];
    validate(messageId, sequenceNumber, contentRelated) {
        if (!Number.isSafeInteger(sequenceNumber) || sequenceNumber < 0)
            return 32;
        if (contentRelated && sequenceNumber % 2 === 0)
            return 35;
        if (!contentRelated && sequenceNumber % 2 !== 0)
            return 34;
        let insertion = this.#received.findIndex((value) => value.messageId > messageId);
        if (insertion < 0)
            insertion = this.#received.length;
        const previous = this.#received[insertion - 1];
        const next = this.#received[insertion];
        if (previous && previous.sequenceNumber > sequenceNumber)
            return 32;
        if (next && next.sequenceNumber < sequenceNumber)
            return 33;
        this.#received.splice(insertion, 0, { messageId, sequenceNumber });
        if (this.#received.length > 1000)
            this.#received.splice(0, this.#received.length - 1000);
        return undefined;
    }
    checkpoint() {
        return this.#received.map((value) => ({ ...value }));
    }
    restore(checkpoint) {
        this.#received.splice(0, this.#received.length, ...checkpoint.map((value) => ({ ...value })));
    }
}
export class AcknowledgementQueue {
    #pending = new Set();
    add(messageId) {
        if (this.#pending.size >= 8192 && !this.#pending.has(messageId))
            throw new RangeError("Acknowledgement queue is full");
        this.#pending.add(messageId);
    }
    drain(maximum = 8192) {
        if (!Number.isSafeInteger(maximum) || maximum < 1 || maximum > 8192)
            throw new RangeError("Invalid ACK batch size");
        const result = [];
        for (const messageId of this.#pending) {
            result.push(messageId);
            this.#pending.delete(messageId);
            if (result.length === maximum)
                break;
        }
        return result;
    }
}
export class PendingMessageCache {
    capacity;
    byteCapacity;
    #messages = new Map();
    #retainedBytes = 0;
    constructor(capacity = 8192, byteCapacity = 32 * 1024 * 1024) {
        this.capacity = capacity;
        this.byteCapacity = byteCapacity;
        if (!Number.isSafeInteger(capacity) || capacity < 1 || capacity > 8192)
            throw new RangeError("Invalid pending-message capacity");
        if (!Number.isSafeInteger(byteCapacity) || byteCapacity < 1)
            throw new RangeError("Invalid pending-message byte capacity");
    }
    get retainedBytes() { return this.#retainedBytes; }
    retain(message) {
        if (this.#messages.size >= this.capacity && !this.#messages.has(message.messageId)) {
            throw new RangeError("Pending-message cache is full");
        }
        const previous = this.#messages.get(message.messageId);
        const nextBytes = this.#retainedBytes - (previous?.body.length ?? 0) + message.body.length;
        if (nextBytes > this.byteCapacity)
            throw new RangeError("Pending-message byte capacity exceeded");
        const body = message.body.slice();
        this.#messages.set(message.messageId, { ...message, body });
        this.#retainedBytes = nextBytes;
    }
    acknowledge(messageIds) {
        for (const messageId of messageIds) {
            const message = this.#messages.get(messageId);
            if (!message)
                continue;
            this.#messages.delete(messageId);
            this.#retainedBytes -= message.body.length;
        }
    }
    resend(messageIds) {
        return messageIds.flatMap((messageId) => {
            const message = this.#messages.get(messageId);
            return message ? [{ ...message, body: message.body.slice() }] : [];
        });
    }
    state(messageIds) {
        return Uint8Array.from(messageIds, (messageId) => this.#messages.has(messageId) ? 0x04 : 0x01);
    }
    dropRpcResult(requestMessageId) {
        for (const [messageId, message] of this.#messages) {
            if (message.body.length < 12)
                continue;
            const view = new DataView(message.body.buffer, message.body.byteOffset, message.body.byteLength);
            if (view.getUint32(0, true) !== 0xf35c6d01 || view.getBigInt64(4, true) !== requestMessageId)
                continue;
            this.#messages.delete(messageId);
            this.#retainedBytes -= message.body.length;
            return { ...message, body: message.body.slice() };
        }
        return undefined;
    }
}
export class BadMessageRecovery {
    #outstanding = new Map();
    add(messageId, sequenceNumber) {
        if (this.#outstanding.size >= 8192 && !this.#outstanding.has(messageId))
            throw new RangeError("Outstanding-message limit reached");
        this.#outstanding.set(messageId, sequenceNumber);
    }
    accept(input) {
        const sequence = this.#outstanding.get(input.badMessageId);
        if (sequence === undefined || sequence !== input.badSequenceNumber)
            return { kind: "fatal" };
        if (input.errorCode === 16 || input.errorCode === 17) {
            if ((input.outerServerMessageId & 1n) !== 1n)
                return { kind: "fatal" };
            return { kind: "time", serverSeconds: Number(input.outerServerMessageId >> 32n) };
        }
        if (input.errorCode === 48)
            return { kind: "salt" };
        return { kind: "fatal" };
    }
}
